import type { ProjectDocumentType } from "@shared/types";
import { useState } from "react";
import { Dialog, DialogOverlay } from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { StudioButton } from "@/features/studio/theme";
import {
	Btn,
	FloatPanel,
	Icn,
	secondaryBtnStyle,
} from "../homeowner/design-primitives";

const DOCUMENT_TYPE_OPTIONS: Array<{
	value: ProjectDocumentType;
	label: string;
}> = [
	{ value: "contract", label: "Contract" },
	{ value: "invoice", label: "Invoice" },
	{ value: "permit", label: "Permit" },
];

interface DocumentUploadDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (data: {
		title: string;
		type: ProjectDocumentType;
		description?: string;
		file: File;
	}) => Promise<void>;
	isSubmitting: boolean;
}

export function DocumentUploadDialog({
	open,
	onOpenChange,
	isSubmitting,
	onSubmit,
}: DocumentUploadDialogProps): React.ReactElement {
	const [title, setTitle] = useState("");
	const [type, setType] = useState<ProjectDocumentType>("contract");
	const [description, setDescription] = useState("");
	const [file, setFile] = useState<File | null>(null);

	const reset = () => {
		setTitle("");
		setType("contract");
		setDescription("");
		setFile(null);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen && !isSubmitting) {
			reset();
		}
		onOpenChange(nextOpen);
	};

	const handleSubmit = async () => {
		if (!file || !title.trim()) return;
		try {
			await onSubmit({
				title: title.trim(),
				type,
				description: description.trim() || undefined,
				file,
			});
			reset();
			onOpenChange(false);
		} catch {
			// Keep dialog open so user can retry
		}
	};

	const canSubmit = title.trim() && file;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogOverlay />
			{open && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 9999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: 24,
						pointerEvents: "none",
					}}
				>
					<div
						role="dialog"
						aria-modal="true"
						style={{
							background: "var(--color-dashboard-paper)",
							borderRadius: 14,
							width: "100%",
							maxWidth: 480,
							boxShadow:
								"0 24px 60px -12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 25%, transparent), 0 8px 24px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent)",
							pointerEvents: "auto",
						}}
					>
						<FloatPanel
							elev={0}
							padding={0}
							style={{ borderRadius: 14, border: "none" }}
						>
							{/* Header */}
							<div
								style={{
									padding: "28px 28px 22px",
									borderBottom: "1px solid var(--color-dashboard-border)",
									display: "flex",
									alignItems: "flex-start",
									justifyContent: "space-between",
								}}
							>
								<div>
									<div
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 9,
											letterSpacing: "0.18em",
											color: "var(--color-dashboard-muted)",
											textTransform: "uppercase",
										}}
									>
										Documents
									</div>
									<h2
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 500,
											fontSize: 26,
											lineHeight: 1.1,
											color: "var(--color-dashboard-text)",
											margin: "6px 0 0",
											letterSpacing: "-0.02em",
										}}
									>
										Upload{" "}
										<span
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												color: "var(--color-dashboard-accent-display)",
											}}
										>
											document
										</span>
									</h2>
								</div>
								<button
									type="button"
									onClick={() => handleOpenChange(false)}
									style={{
										background: "none",
										border: "none",
										cursor: "pointer",
										padding: 4,
										color: "var(--color-dashboard-muted)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
										marginTop: 4,
									}}
								>
									<Icn
										name="x"
										size={18}
										color="var(--color-dashboard-muted)"
									/>
								</button>
							</div>

							{/* Body */}
							<div
								style={{
									padding: "22px 28px",
									display: "flex",
									flexDirection: "column",
									gap: 20,
								}}
							>
								{/* Title */}
								<div>
									<label
										htmlFor="doc-title"
										style={{
											display: "block",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted-dark)",
											marginBottom: 7,
											letterSpacing: "0.03em",
											textTransform: "uppercase",
										}}
									>
										Title
									</label>
									<input
										id="doc-title"
										type="text"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										placeholder="e.g. Contractor agreement"
										style={{
											width: "100%",
											height: 42,
											padding: "0 14px",
											borderRadius: 7,
											border: "1px solid var(--color-border-default)",
											background: "var(--color-dashboard-paper)",
											fontFamily: "var(--font-body)",
											fontSize: 14,
											color: "var(--color-dashboard-text)",
											outline: "none",
											boxSizing: "border-box",
											transition: "border-color 150ms ease",
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor =
												"var(--color-dashboard-accent)";
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor =
												"var(--color-border-default)";
										}}
									/>
								</div>

								{/* Document type */}
								<div>
									<span
										id="doc-type-label"
										style={{
											display: "block",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted-dark)",
											marginBottom: 7,
											letterSpacing: "0.03em",
											textTransform: "uppercase",
										}}
									>
										Type
									</span>
									<Select
										value={type}
										onValueChange={(v) => setType(v as ProjectDocumentType)}
									>
										<SelectTrigger
											id="doc-type"
											aria-labelledby="doc-type-label"
											style={{
												width: "100%",
												height: 42,
												borderRadius: 7,
												border: "1px solid var(--color-border-default)",
												background: "var(--color-dashboard-paper)",
												fontFamily: "var(--font-body)",
												fontSize: 14,
												color: "var(--color-dashboard-text)",
											}}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{DOCUMENT_TYPE_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{/* Description */}
								<div>
									<label
										htmlFor="doc-description"
										style={{
											display: "block",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted-dark)",
											marginBottom: 7,
											letterSpacing: "0.03em",
											textTransform: "uppercase",
										}}
									>
										Description{" "}
										<span
											style={{
												fontWeight: 400,
												color: "var(--color-dashboard-muted)",
											}}
										>
											(optional)
										</span>
									</label>
									<textarea
										id="doc-description"
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										placeholder="Add context about this file"
										rows={3}
										style={{
											width: "100%",
											padding: "10px 14px",
											borderRadius: 7,
											border: "1px solid var(--color-border-default)",
											background: "var(--color-dashboard-paper)",
											fontFamily: "var(--font-body)",
											fontSize: 14,
											color: "var(--color-dashboard-text)",
											outline: "none",
											resize: "vertical",
											boxSizing: "border-box",
											transition: "border-color 150ms ease",
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderColor =
												"var(--color-dashboard-accent)";
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor =
												"var(--color-border-default)";
										}}
									/>
								</div>

								{/* File */}
								<div>
									<label
										id="doc-file-label"
										htmlFor="doc-file-input"
										style={{
											display: "block",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted-dark)",
											marginBottom: 7,
											letterSpacing: "0.03em",
											textTransform: "uppercase",
										}}
									>
										File
									</label>
									{file ? (
										<div
											style={{
												padding: "14px 16px",
												borderRadius: 7,
												border:
													"1px solid var(--color-dashboard-success-border)",
												background: "var(--color-dashboard-success-bg)",
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												gap: 12,
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
													minWidth: 0,
												}}
											>
												<Icn
													name="fileText"
													size={16}
													color="var(--color-dashboard-success)"
												/>
												<span
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 13.5,
														color: "var(--color-dashboard-success)",
														fontWeight: 500,
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{file.name}
												</span>
											</div>
											<button
												type="button"
												onClick={() => setFile(null)}
												style={{
													background: "none",
													border: "none",
													cursor: "pointer",
													padding: 2,
													flexShrink: 0,
													display: "flex",
													alignItems: "center",
												}}
											>
												<Icn
													name="x"
													size={16}
													color="var(--color-dashboard-success)"
												/>
											</button>
										</div>
									) : (
										<label
											htmlFor="doc-file-input"
											aria-labelledby="doc-file-label"
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												justifyContent: "center",
												gap: 8,
												padding: "24px 20px",
												borderRadius: 7,
												border:
													"1.5px dashed var(--color-dashboard-muted-light)",
												background: "var(--color-dashboard-surface)",
												cursor: "pointer",
												transition:
													"border-color 150ms ease, background 150ms ease",
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.borderColor =
													"var(--color-dashboard-accent)";
												e.currentTarget.style.background =
													"var(--color-dashboard-surface)";
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.borderColor =
													"var(--color-dashboard-muted-light)";
												e.currentTarget.style.background =
													"var(--color-dashboard-surface)";
											}}
										>
											<Icn
												name="upload"
												size={24}
												color="var(--color-dashboard-muted)"
											/>
											<div
												style={{
													fontFamily: "var(--font-body)",
													fontSize: 13,
													color: "var(--color-dashboard-muted)",
													textAlign: "center",
													lineHeight: 1.5,
												}}
											>
												<span
													style={{
														color: "var(--color-dashboard-accent-dark)",
														fontWeight: 600,
													}}
												>
													Click to choose
												</span>{" "}
												or drag and drop
												<br />
												<span
													style={{
														fontSize: 11.5,
														color: "var(--color-dashboard-muted)",
													}}
												>
													PDF, JPEG, PNG, WebP — up to 10MB
												</span>
											</div>
											<input
												id="doc-file-input"
												data-testid="doc-file-input"
												type="file"
												accept=".pdf,image/jpeg,image/png,image/webp"
												onChange={(e) => setFile(e.target.files?.[0] ?? null)}
												style={{ display: "none" }}
											/>
										</label>
									)}
								</div>
							</div>

							{/* Footer */}
							<div
								style={{
									padding: "16px 28px 26px",
									display: "flex",
									justifyContent: "flex-end",
									gap: 10,
								}}
							>
								<StudioButton
									variant="secondary"
									size="md"
									onClick={() => handleOpenChange(false)}
									disabled={isSubmitting}
									style={secondaryBtnStyle("md")}
								>
									Cancel
								</StudioButton>
								<Btn
									variant="primary"
									size="md"
									icon={
										<Icn
											name="upload"
											size={16}
											color="var(--color-dashboard-on-fill)"
										/>
									}
									onClick={() => void handleSubmit()}
									disabled={!canSubmit || isSubmitting}
								>
									{isSubmitting ? "Uploading..." : "Upload"}
								</Btn>
							</div>
						</FloatPanel>
					</div>
				</div>
			)}
		</Dialog>
	);
}
